import React, { useState, useEffect } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { Loader2 } from "lucide-react"; // スピナー用
import { isEnglishOperatorMode, isMainSalesBreakerHost, rememberOperatorSurfaceFromLocation, withOperatorSurface } from "../utils/operatorMode";

// 🚀 修正点1: API_URL を動的に（Localhost または 現在のドメイン）
const API_URL = window.location.hostname === 'localhost' 
  ? (process.env.REACT_APP_API_URL || "http://localhost:3173") 
  : window.location.origin;

function ResetPasswordPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [token, setToken] = useState(null);
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const isOemDomain = !isMainSalesBreakerHost(window.location.hostname);
  const fallbackBrandName = isOemDomain ? "Partner Portal" : "Sales Breaker";
  const isEnglishBeta = isEnglishOperatorMode() && !isOemDomain;
  
  // 🚀 修正点2: ブランディング情報の保持
  const [oemBranding, setOemBranding] = useState(null);

  useEffect(() => {
    rememberOperatorSurfaceFromLocation(window.location.search);
    // 1. トークンの取得
    const resetToken = searchParams.get('token');
    if (!resetToken) {
      setError(isEnglishBeta ? 'This reset link is invalid. Please request a new password reset email.' : '無効なリンクです。パスワードリセットをもう一度やり直してください。');
    }
    setToken(resetToken);

    // 2. 🚀 ブランディング情報の取得（カメレオン化）
    const fetchBranding = async () => {
      try {
        const res = await fetch(`${API_URL}/api/public/oem/branding-by-domain?hostname=${window.location.hostname}`);
        const data = await res.json();
        
        if (data.success && data.is_oem) {
          setOemBranding(data.branding);
          document.title = `${data.branding.display_name} | パスワード再設定`;

          // ファビコンの動的書き換え
          if (data.branding.logo_path) {
            const iconUrl = `${window.location.origin}/api/oem${data.branding.logo_path}`;
            const existingIcons = document.querySelectorAll("link[rel*='icon']");
            existingIcons.forEach(el => el.parentNode.removeChild(el));
            const link = document.createElement('link');
            link.type = 'image/png';
            link.rel = 'shortcut icon';
            link.href = iconUrl;
            document.getElementsByTagName('head')[0].appendChild(link);
          }
        }
      } catch (e) {
        console.error("Branding fetch error:", e);
      }
    };
    fetchBranding();
    if (isEnglishBeta) {
      document.title = "SalesBreaker | Set New Password";
    }
  }, [searchParams, isEnglishBeta]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (password !== confirmPassword) {
      setError(isEnglishBeta ? 'Passwords do not match.' : 'パスワードが一致しません。');
      return;
    }
    if (password.length < 8) {
      setError(isEnglishBeta ? 'Password must be at least 8 characters.' : 'パスワードは8文字以上で設定してください。');
      return;
    }

    setIsLoading(true);
    setError('');
    setMessage('');

    try {
      const response = await fetch(`${API_URL}/api/reset-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, password, confirmPassword }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || (isEnglishBeta ? 'Could not update your password.' : 'パスワードの更新に失敗しました。'));
      }
      
      setMessage(isEnglishBeta ? 'Password updated. Redirecting to login.' : 'パスワードが正常に更新されました。ログインページに移動します。');
      // 🚀 修正：ログインページへの遷移も念のため3秒待機
      setTimeout(() => navigate(withOperatorSurface('/login')), 3000);

    } catch (err) {
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="flex items-center justify-center min-h-screen bg-gray-100 p-3 sm:p-4">
      <div className="w-full max-w-md p-5 sm:p-8 space-y-6 sm:space-y-8 bg-white shadow-xl rounded-2xl sm:rounded-3xl border border-slate-100">
        <div className="text-center">
           <Link to="/" className="inline-block mb-4 h-16">
              {/* 🚀 修正点3: OEMロゴを表示 */}
              <img 
                src={oemBranding?.logo_path ? `${window.location.origin}/api/oem${oemBranding.logo_path}` : "/images/logo.png"} 
                alt="Logo" 
                className="max-h-full w-auto mx-auto" 
                onError={(e) => { 
                  e.target.onerror = null; 
                  e.target.src=isOemDomain ? 'https://placehold.co/128x128/6366f1/ffffff?text=Logo' : 'https://placehold.co/128x128/6366f1/ffffff?text=SB'; 
                }}
              />
          </Link>
          <h1 className="text-2xl font-black tracking-tight text-gray-900 break-words">
            {oemBranding?.display_name || fallbackBrandName}
          </h1>
          <p className="text-sm text-gray-500 mt-2">{isEnglishBeta ? "Set a new password" : "新しいパスワードの設定"}</p>
        </div>

        <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
          <div className="space-y-4">
            <input
              type="password"
              required
              className="w-full px-4 py-3 bg-white border border-slate-300 rounded-xl focus:ring-2 focus:ring-indigo-500 outline-none transition-all"
              placeholder={isEnglishBeta ? "New password" : "新しいパスワード"}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
            <input
              type="password"
              required
              className="w-full px-4 py-3 bg-white border border-slate-300 rounded-xl focus:ring-2 focus:ring-indigo-500 outline-none transition-all"
              placeholder={isEnglishBeta ? "Confirm new password" : "新しいパスワード（確認用）"}
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
            />
          </div>
          
          {message && (
            <div className="p-4 bg-green-50 border border-green-200 rounded-xl text-sm text-green-700 font-bold animate-pulse">
              {message}
            </div>
          )}
          {error && (
            <div className="p-4 bg-red-50 border border-red-200 rounded-xl text-sm text-red-600 font-bold">
              {error}
            </div>
          )}

          <button
            type="submit"
            disabled={isLoading || !token}
            className="w-full py-4 bg-indigo-600 text-white rounded-xl font-bold transition-all shadow-lg active:scale-[0.98] disabled:bg-indigo-400"
            style={{ backgroundColor: oemBranding?.brand_color || '#4f46e5' }}
          >
            {isLoading ? <Loader2 className="animate-spin mx-auto" size={24} /> : (isEnglishBeta ? 'Update password' : 'パスワードを更新')}
          </button>
        </form>
      </div>
    </div>
  );
}

export default ResetPasswordPage;
